import { EnumValue } from '@lucidtech/las-sdk-browser';
import { FieldApi } from '@tanstack/react-form';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useFocus } from 'react-aria';

import { merge } from '@/components';
import { Flex, Input, Popover, Text, TextArea, TextField } from '@/components/core';
import { EnumMenu, FieldLabel, PredictionInfoIcon } from '@/components/ui';
import { FORMATTER_CLASSIFICATION_FUNCTION_ID } from '@/constants';
import { useFormatters, useFormContext, useValidators } from '@/hooks';
import {
  FieldValueState,
  FormValue,
  FormValueRow,
  FormValues,
  selectFromFieldConfig,
  useDocumentStore,
  useValidationStore,
} from '@/store';
import {
  DivProps,
  FieldContextType,
  FieldMetaExtended,
  FieldStateArrayType,
  FieldStateType,
  FieldStateValueType,
  isNumber,
  OmitChildren,
  toString,
} from '@/utils';

const toFieldState = (formValue: FormValue): FieldStateValueType => {
  return {
    rawValue: formValue.current.rawValue,
    value: formValue.current.value,
    confidence: formValue.current.confidence,
  };
};

type OnChangeAsync = {
  value: FormValue | FormValueRow[];
  fieldApi: FieldApi<FormValues, string, undefined, undefined, FormValue | FormValueRow[]>;
  signal: AbortSignal;
};

export type FieldWrapperProps = OmitChildren<DivProps> & {
  hideLabel?: boolean;
  isTableField?: boolean;
  isMultiLine?: boolean;
  name: string;
  rowId: string;
};

const findClosestPage = (
  currentValue: string | null | undefined,
  currentPage: number,
  candidates: FieldValueState[]
): FieldValueState | null => {
  let closestCandidate: FieldValueState | null = null;
  let minDistance = Infinity;

  candidates.forEach((candidate) => {
    if (candidate.value === currentValue && isNumber(candidate.page)) {
      const distance = Math.abs(currentPage - candidate.page);
      if (distance < minDistance) {
        closestCandidate = candidate;
        minDistance = distance;
      }
    }
  });

  return closestCandidate;
};

type FieldProps = {
  field: FieldApi<FormValues, string, undefined, undefined, FormValue | FormValueRow[]>;
} & FieldWrapperProps;

const IField = ({ field, name, hideLabel, isMultiLine, isTableField, className }: FieldProps) => {
  const hideTableView = useValidationStore((state) => state.hideTableView);
  const scrollToPage = useValidationStore((state) => state.scrollToPage);
  const setDetachTable = useValidationStore((state) => state.setDetachTable);

  const config = useDocumentStore(selectFromFieldConfig(name));

  const setFocusedField = useDocumentStore((state) => state.setFocusedField);
  const focusedField = useDocumentStore((state) => state.focusedField);
  const currentPages = useDocumentStore((state) => state.currentPages);
  const isEditing = useDocumentStore((state) => state.isEditing);
  const isLoading = useDocumentStore((state) => state.isLoading);

  const form = useFormContext();
  const fieldValue = form.getFieldValue(name);

  const firstFormatter = useMemo(() => config?.formatters?.[0], [config]);
  const formatterTriggerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isEnum = useMemo(() => firstFormatter?.functionId === FORMATTER_CLASSIFICATION_FUNCTION_ID, [firstFormatter]);

  const enumValues = useMemo(() => {
    const enumValues: EnumValue[] = [];

    for (const label of (firstFormatter?.config.labels as EnumValue[] | undefined) ?? []) {
      enumValues.push({ value: label.value, description: label.description });
    }

    const catchAll = firstFormatter?.config.catchAll as string | undefined;
    if (catchAll) {
      enumValues.push({ value: catchAll, description: catchAll });
    }

    return enumValues;
  }, [firstFormatter]);

  const defaultValue = fieldValue?.current?.value ?? fieldValue?.current?.rawValue;
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);
  const [isFocusedActual, setIsFocused] = useState(false);
  const isFocused = useMemo(
    () => isFocusedActual || focusedField?.id === name,
    [focusedField?.id, isFocusedActual, name]
  );

  const formValue = field.state.value as FormValue;

  const onChange = useCallback(
    (rawValue: string) => {
      setInputValue(rawValue);
      field.setValue({
        ...formValue,
        current: {
          ...formValue.current,
          rawValue,
          page: currentPages[0],
        },
      });
    },
    [currentPages, field, formValue]
  );

  const onFocus = useCallback(() => {
    setInputValue(fieldValue?.current?.rawValue);
    setIsFocused(true);

    field.setMeta((meta: FieldMetaExtended) => ({
      ...meta,
      isReviewed: true,
    }));

    const closestCandidate = findClosestPage(formValue.current.value, currentPages[0], formValue.candidates);

    if (closestCandidate) {
      setFocusedField({ ...closestCandidate, id: name, name: config?.name, onChange });
      if (isNumber(closestCandidate?.page) && !currentPages.includes(closestCandidate.page)) {
        scrollToPage?.(closestCandidate.page);
      }
    } else {
      setFocusedField({ ...formValue.current, id: name, name: config?.name, onChange });
      if (isNumber(formValue.current.page)) {
        scrollToPage?.(formValue.current.page);
      }
    }

    window.requestAnimationFrame(() => {
      // Hide the table view if a field which is not in the table form receives focus
      if (!isTableField) {
        hideTableView(true);
        setDetachTable(false);
      }
    });
  }, [
    config?.name,
    currentPages,
    field,
    fieldValue,
    formValue,
    hideTableView,
    isTableField,
    name,
    onChange,
    scrollToPage,
    setDetachTable,
    setFocusedField,
  ]);

  const onChangeEnum = useCallback(
    (fieldValue: FieldValueState) => {
      setInputValue(fieldValue?.value ?? fieldValue.rawValue ?? '');

      field.setValue({
        ...formValue,
        current: {
          ...structuredClone(fieldValue),
        },
      });
    },
    [field, formValue]
  );

  const fieldMeta = field.getMeta() as FieldMetaExtended;

  const hasFormatterWarnings = (fieldMeta.formatters ?? []).filter((e) => e.warnings.length > 0).length > 0;
  const hasFormatterErrors = (fieldMeta.formatters ?? []).filter((e) => e.errors.length > 0).length > 0;
  const formatterErrors = (fieldMeta.formatters ?? []).map((e) => e.errors).flat();
  const formatterWarnings = (fieldMeta.formatters ?? []).map((e) => e.warnings).flat();
  const hasFormatterWarningsOrErrors = hasFormatterWarnings || hasFormatterErrors;

  const all = [...(fieldMeta.formatters ?? []), ...(fieldMeta.validators ?? [])];
  const hasWarnings = all.filter((e) => e.warnings.length > 0).length > 0;
  const hasErrors = all.filter((e) => e.errors.length > 0).length > 0;

  const onBlur = useCallback(() => {
    setIsFocused(false);

    if (!hasErrors) {
      setInputValue(formValue.current.value ?? formValue.current.rawValue ?? '');
    }
    field.handleBlur();
  }, [field, formValue, hasErrors]);

  const { focusProps } = useFocus({ onFocus, onBlur });

  if (isLoading || !formValue) {
    return (
      <Flex col className="gap-sm">
        <FieldLabel name={config?.name || name} description={toString(config?.description)} hideLabel={hideLabel} />
        <div className="h-[1.95rem] rounded-md border-1 border-gray-200 bg-white p-[0.4rem]">
          <div className="animate-fade-in-pulse h-full w-full rounded-md bg-gray-200" />
        </div>
      </Flex>
    );
  }

  return (
    <div
      className={merge(
        'flex flex-col gap-2',
        isTableField && !isFocused && hasWarnings && !fieldMeta.isReviewed && 'bg-warning-25',
        isTableField && !isFocused && hasErrors && 'bg-error-50',
        isTableField && isFocused && 'bg-primary-25',
        !isEditing && 'bg-gray-50',
        className
      )}
    >
      <div className="flex flex-col gap-1">
        {/* autoComplete="off": Disable autocomplete for plugins such as LastPass */}
        <TextField
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          value={inputValue ?? defaultValue}
          onChange={onChange}
        >
          <FieldLabel name={config?.name || name} description={toString(config?.description)} hideLabel={hideLabel} />

          <Popover
            className="border-none p-0"
            placement="top right"
            isNonModal
            offset={-8}
            triggerRef={formatterTriggerRef}
            isOpen={name === focusedField?.id && hasFormatterWarningsOrErrors}
          >
            <div
              className={merge(
                'bg-error-600 flex max-w-[12rem] items-center rounded-sm px-1.5 py-0.5 shadow-lg',
                hasFormatterErrors ? 'bg-error-600' : 'bg-warning-600'
              )}
            >
              {formatterErrors.map((e) => (
                <Text className="text-xs font-semibold text-white" slot="description">
                  {e}
                </Text>
              ))}
              {formatterWarnings.map((w) => (
                <Text className="text-xs font-semibold text-white" slot="description">
                  {w}
                </Text>
              ))}
            </div>
          </Popover>

          {/* autoComplete="off": Disable autocomplete for plugins such as LastPass */}
          {isMultiLine && isFocused ? (
            <TextArea
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              disabled={!isEditing}
              id={name}
              name={field.name}
              ref={formatterTriggerRef}
              className={merge(
                'field-sizing-content w-full resize-none border-0',
                isFocused && 'border-primary-300 border'
              )}
              {...focusProps}
              autoFocus
            >
              <PredictionInfoIcon
                fieldName={name}
                formattedValue={formValue.current.value}
                confidence={formValue.current.confidence}
                formatters={fieldMeta.formatters}
                validators={fieldMeta.validators}
                isTableField={isTableField}
                isTouched={fieldMeta.isReviewed}
              />
            </TextArea>
          ) : (
            <Input
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              disabled={!isEditing}
              ref={formatterTriggerRef}
              props={{
                container: {
                  className: merge(
                    'gap-1',
                    hasWarnings && !fieldMeta.isReviewed && 'border-warning-300',
                    hasErrors && 'border-error-300',
                    isTableField && 'border-none shadow-none bg-transparent',
                    isFocused && 'border-primary-300 border'
                  ),
                },
              }}
              id={name}
              name={field.name}
              type="text"
              className="w-full"
              {...focusProps}
            >
              {isEnum && isEditing ? (
                <EnumMenu formValue={formValue} enumValues={enumValues} onAction={onChangeEnum} />
              ) : null}
              <PredictionInfoIcon
                fieldName={name}
                formattedValue={formValue.current.value}
                confidence={formValue.current.confidence}
                formatters={fieldMeta.formatters}
                validators={fieldMeta.validators}
                isTableField={isTableField}
                isTouched={fieldMeta.isReviewed}
              />
            </Input>
          )}
        </TextField>
      </div>
    </div>
  );
};

const IFieldWrapper = ({ hideLabel = false, isTableField, name, rowId, className, ...rest }: FieldWrapperProps) => {
  const form = useFormContext();
  const formState = form.state;
  const documentId = useDocumentStore((state) => state.documentId);
  const config = useDocumentStore(selectFromFieldConfig(name));

  const { run: runFormatters } = useFormatters({ name });
  const { run: runValidators } = useValidators({ name });

  // TODO: Consider adding fieldApi or form as argument to this callback instead of relying on formState.values
  const createContext = useCallback(() => {
    const fields: FieldStateType = {};

    for (const [name_, formValue_] of Object.entries(formState.values)) {
      if (Array.isArray(formValue_)) {
        const rows: FieldStateArrayType = [];
        for (const formRows of formValue_) {
          const row: Record<string, FieldStateValueType[]> = {};
          for (const [subName, subFormValue] of Object.entries(formRows.columns)) {
            row[subName] = [toFieldState(subFormValue)];
          }
          rows.push(row);
        }
        fields[name_] = rows;
      } else {
        fields[name_] = [toFieldState(formValue_)];
      }
    }
    const context: FieldContextType = {
      name: name,
      fields: fields,
      config: config,
    };

    return context;
  }, [config, formState.values, name]);

  const onChangeAsync = useCallback(
    async (field: OnChangeAsync) => {
      const isTouched = field.fieldApi.getMeta().isDirty;
      if (!isTouched) return;

      const formValue = field.value as FormValue;
      const initialState = toFieldState(formValue);
      initialState.isTouched = isTouched;
      initialState.value = undefined;
      const context = createContext();

      const { result: formattedState, meta: formattingMeta } = await runFormatters(initialState, context);
      const errors = formattingMeta.filter((e) => e.errors.length > 0);

      if (errors.length > 0) {
        field.fieldApi.setMeta((meta: FieldMetaExtended) => ({
          ...meta,
          errors,
          formatters: formattingMeta,
          validators: [],
        }));

        return errors;
      }

      context.fields[name] = [formattedState ?? {}];

      const { result: validatedState, meta: validationMeta } = await runValidators(formattedState, context);
      errors.push(...validationMeta.filter((e) => e.errors.length > 0));

      field.fieldApi.setMeta((meta: FieldMetaExtended) => ({
        ...meta,
        errors,
        formatters: formattingMeta,
        validators: validationMeta,
      }));

      if (errors.length > 0) {
        return errors;
      }

      const current = {
        ...formValue.current,
        ...formattedState,
        ...validatedState,
        isValidated: true,
      };

      field.fieldApi.form.setFieldValue(name, { ...formValue, current });
    },
    [createContext, name, runFormatters, runValidators]
  );

  return (
    <form.Field
      key={`${documentId}/${rowId}`}
      name={name}
      validators={{
        onChangeAsyncDebounceMs: 200,
        onChangeAsync: onChangeAsync,
      }}
      children={(field) => (
        <IField
          field={field}
          name={name}
          rowId={rowId}
          hideLabel={hideLabel}
          isTableField={isTableField}
          className={className}
          {...rest}
        />
      )}
    />
  );
};

export const Field = memo(IFieldWrapper) as typeof IFieldWrapper;
